import { useEffect, useState } from 'react';
import HeaderButtons from '@/components/AppointmentBookingModal/HeaderButtons';
import HeaderNav from '@/components/AppointmentBookingModal/HeaderNav';
import LocationPage from '@/components/AppointmentBookingModal/LocationPage';
import ServicePage from '@/components/AppointmentBookingModal/ServicePage';
import DateTimePage from '@/components/AppointmentBookingModal/DateTimePage';
import ConfirmPage from '@/components/AppointmentBookingModal/ConfirmPage';
import SuccessPage from '@/components/AppointmentBookingModal/SuccessPage';
import useStore from '@/store/useStore';
import beTwMerge from '@/utils/beTwMerge';
import type { IDataShowroom } from '@/components/BuilderIO/common/CommonFields';
import { getServiceName } from '@/utils/showroom';
import { NavigationEvents } from '@/components/NavigationEvents/NavigationEvents';

/**
 * AppointmentBookingModal component renders the modal for booking appointments.
 * It handles the state of the modal pages, user information, and appointment details.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.user - The user object.
 * @param {Function} props.onClose - The function to close the modal.
 */
export default function AppointmentBookingModal({ user, onClose }) {
  const info = useStore(state => state.appointmentBookingInformation);
  const [confirmDateError, setConfirmDateError] = useState(false);
  const { activePage } = info;
  const setInfo = useStore(state => state.setAppointmentBookingInformation);
  const activePageNames = {
    1: 'select a location',
    2: 'select a service',
    3: 'select date and time',
    4: 'confirmation',
  };
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const timezones = {
    PST: 'PT', // Pacific Standard Time
    PDT: 'PT', // Pacific Daylight Time
    EST: 'ET', // Eastern Standard Time
    EDT: 'ET', // Eastern Daylight Time
    CST: 'CT', // Central Standard Time
    CDT: 'CT', // Central Daylight Time
    MST: 'MT', // Mountain Standard Time
    MDT: 'MT', // Mountain Daylight Time
    AKST: 'AKT', // Alaska Standard Time
    AKDT: 'AKT', // Alaska Daylight Time
    HST: 'HST', // Hawaii-Aleutian Standard Time (Hawaii does not observe DST)
  };

  const dateSelected = info.selectedDate
    ? `${days[new Date(info.selectedDate).getDay()]}, ${months[new Date(info.selectedDate).getMonth()]} ${new Date(info.selectedDate).getDate()}`
    : '';
  const timeSelected = info.selectedDate ? `${info.selectedTime}` : '';
  const dateTimeSelected = info.selectedDate
    ? `${days[new Date(info.selectedDate).getDay()]}, ${months[new Date(info.selectedDate).getMonth()]} ${new Date(info.selectedDate).getDate()} - ${info.selectedTime}`
    : '';

  const [builderShowroomsCopy, setBuilderShowroomsCopy] = useState<IDataShowroom[]>([]);
  const setShowroomCityId = useStore(state => state.setShowroomCityId);

  /**
   * useEffect hook to set the city ID for the selected showroom.
   * It fetches the city details and updates the state with the city ID, timezone ID, and timezone abbreviation.
   */
  useEffect(() => {
    async function setCityId() {
      try {
        const cityDetails = await setShowroomCityId(info.selectedShowroomStore, 'setster_id');

        setInfo('selectedShowroomStore', {
          ...info.selectedShowroomStore,
          id: cityDetails.id,
          timezoneId: cityDetails.timezoneId,
          timezoneAbreviation: timezones[cityDetails.timezoneAbreviation],
        });
        setInfo('prevSelectedShowroomStore', {
          ...info.prevSelectedShowroomStore,
          id: cityDetails.id,
          timezoneId: cityDetails.timezoneId,
          timezoneAbreviation: timezones[cityDetails.timezoneAbreviation],
        });
        setInfo('selectedTimezoneId', '0');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }

    if (!info.selectedShowroomStore.timezoneAbreviation) {
      setCityId();
    }
  }, []);

  const pages = {
    1: (
      <LocationPage
        user={user}
        info={info}
        setInfo={setInfo}
        builderShowroomsCopy={builderShowroomsCopy}
        setBuilderShowroomsCopy={setBuilderShowroomsCopy}
      />
    ),
    2: (
      <ServicePage
        user={user}
        info={info}
        setInfo={setInfo}
        builderShowroomsCopy={builderShowroomsCopy}
        setBuilderShowroomsCopy={setBuilderShowroomsCopy}
      />
    ),
    3: <DateTimePage setInfo={setInfo} info={info} setConfirmDateError={setConfirmDateError} />,
    4: (
      <ConfirmPage
        confirmDateError={confirmDateError}
        setConfirmDateError={setConfirmDateError}
        info={info}
        setInfo={setInfo}
      />
    ),
    5: <SuccessPage info={info} />,
  };

  return (
    <div
      className={beTwMerge(
        'modal tw-w-full tw-h-full lg:tw-max-h-[744px] tw-bg-white tw-scrollbar-none lg:tw-bg-transparent lg:tw-w-[95%] lg:tw-max-w-[1142px] lg:tw-min-w-[780px] tw-flex tw-top-[50%] -tw-translate-y-1/2 tw-left-[50%] -tw-translate-x-1/2'
      )}
      id="book-appointment"
      role="dialog"
      aria-labelledby="myModalBookAppointment"
    >
      <NavigationEvents />

      <div
        className={beTwMerge(
          'modal-dialog tw-mb-0 lg:tw-m-4 tw-p-0 tw-w-full   lg:tw-h-[712px]',
          activePage === 1 || activePage === 3 ? 'tw-h-fit' : 'tw-h-ful'
        )}
      >
        <div
          className={beTwMerge(
            'modal-content tw-h-full lg:tw-h-full lg:tw-max-h-[712px] tw-relative tw-border-none lg:tw-flex lg:tw-flex-col',
            activePage === 1 ? 'lg:tw-pb-12' : ''
          )}
        >
          <div
            className={beTwMerge(
              'tw-w-full lg:tw-pb-8 lg:tw-border-b lg:tw-border-t-0 tw-sticky tw-top-0 lg:tw-border-r-0 lg:tw-border-l-0 lg:tw-border-b-[#D9D9D9] lg:tw-border-solid tw-bg-white tw-z-50',
              activePage === 5 && 'lg:tw-border-none'
            )}
          >
            <HeaderButtons
              activePageNames={activePageNames}
              info={info}
              setInfo={setInfo}
              activePage={activePage}
              onClose={onClose}
            />
            {activePage < 5 ? (
              <HeaderNav
                activePageNames={activePageNames}
                setInfo={setInfo}
                activePage={activePage}
                info={info}
                dateSelected={dateSelected}
                timeSelected={timeSelected}
              />
            ) : (
              ''
            )}

            {(activePage > 1 && activePage < 5) ||
            info.selectedShowroomStore.modalName ||
            info.selectedServiceName ||
            dateTimeSelected ? (
              <div
                className={beTwMerge('lg:tw-hidden', activePage === 5 ? 'tw-hidden' : '')}
                style={{ borderBottom: '1px solid #D9D9D9' }}
              >
                <h4
                  className={beTwMerge(
                    'tw-m-0 tw-font-[350] tw-text-xs tw-text-color-grey-700 tw-text-center tw-py-3 lg:tw-hidden',
                    confirmDateError && 'tw-pb-0'
                  )}
                >
                  {activePage > 1 || info.selectedShowroomStore.modalName ? info.selectedShowroomStore.modalName : null}
                  {activePage <= 3 && info.selectedServiceName && !info.selectedTime
                    ? `, ${info.selectedServiceName}`
                    : null}
                  <br />
                  {activePage > 2 || (info.selectedDate && info.selectedTime) ? (
                    <>
                      {info.selectedDate && info.selectedTime && (
                        <span>{getServiceName(info.selectedServiceName, info.isSelectedDateNightTime)}</span>
                      )}
                      <span className="tw-block">
                        {dateSelected}
                        {timeSelected && timeSelected !== 'null' ? ` | ${timeSelected}` : ''}
                      </span>
                    </>
                  ) : null}
                </h4>
                {confirmDateError && (
                  <p className="tw-text-[#C21419] tw-text-center tw-mt-2 tw-mb-4 tw-text-xs">
                    The appointment time you selected is no longer available. Please select another date & time.
                  </p>
                )}
              </div>
            ) : (
              <div className="tw-w-full tw-h-[49px] lg:tw-hidden" style={{ borderBottom: '1px solid #D9D9D9' }} />
            )}
          </div>

          <div
            className={beTwMerge(
              'tw-overflow-scroll tw-scrollbar-none lg:tw-pb-0 tw-pb-8',
              activePage === 1 ? 'tw-pb-0' : '',
              activePage === 4 ? 'lg:tw-h-full' : ''
            )}
          >
            {pages[activePage]}
          </div>
        </div>
      </div>
    </div>
  );
}
